/* eslint-disable react-hooks/rules-of-hooks */
import React, { FC, useCallback, useEffect, useState } from "react";
import { ButtonBase, Card, CardContent, Typography } from "@mui/material";
import { Theme } from "@mui/material/styles";
import PhotoAlbum from "react-photo-album";
import { colors } from "common";
import { cloneDeep } from "lodash";
import { makeStyles } from "@mui/styles";
import ImagesGallery from "./ImagesGallery";
import { PhotosData } from "modules/builds-gallery/types";
import BazarImage from "components/BazarImage";

type ImageWithDimensions = {
  src?: string;
  height?: number;
  width?: number;
};

const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      marginTop: theme.spacing(5),
    },
    header: {
      backgroundColor: colors.primary,
      padding: theme.spacing(1),
      paddingLeft: theme.spacing(5),
      paddingRight: theme.spacing(5),
    },
    cardContentBody: {
      padding: theme.spacing(1),
    },
  }),
  { index: 999 }
);

interface Props {
  photos?: PhotosData[];
}

const component: FC<Props> = ({ photos = [] }) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const classes = useStyles();

  const [loadedPhotos, setLoadedPhotos] = useState([]);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);

  type ImageDimensions = {
    width: number;
    height: number;
  };

  const getImageDimensions = (url: string): Promise<ImageDimensions> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () =>
        resolve({
          width: img.width,
          height: img.height,
        });
      img.onerror = () =>
        resolve({
          width: 0,
          height: 0,
        });
      img.src = url;
    });
  };

  const getImagesWithDimensions = (imagesArg: ImageWithDimensions) => {
    const breakpoints = [1080, 640, 384, 256, 128, 96, 64, 48];
    const imagesWithDimensions = {
      src: imagesArg?.src,
      width: imagesArg?.width,
      height: imagesArg?.height,
      images: breakpoints.map((breakpoint) => {
        const height = Math.round(
          (imagesArg?.height / imagesArg?.width) * breakpoint
        );
        return {
          src: imagesArg?.src,
          width: breakpoint,
          height,
        };
      }),
    };
    return imagesWithDimensions;
  };

  const getDimensionsOfImage = async (imagesArg: PhotosData) => {
    const imgDimensions = await getImageDimensions(imagesArg.url);
    const newImg = {
      src: imagesArg?.url,
      height: imgDimensions && imgDimensions.height ? imgDimensions.height : 0,
      width: imgDimensions && imgDimensions.width ? imgDimensions.width : 0,
    };
    return newImg;
  };
  const loadPhotos = useCallback(async () => {
    const newPhotos = cloneDeep(photos || []);
    const filteredImgs = newPhotos?.filter((z) => z.url);
    for (const img of filteredImgs) {
      const imgWithDimension = await getDimensionsOfImage(img);
      const imgsWithDimension = getImagesWithDimensions(imgWithDimension);
      setLoadedPhotos((prevPhotos) => [...prevPhotos, imgsWithDimension]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos]);

  const onPhotoClicked = (photo) => {
    const selectedPhotoIndex = photos.findIndex((x) => x.url === photo.src);
    setIsGalleryOpen(true);
    setActivePhotoIndex(selectedPhotoIndex > -1 ? selectedPhotoIndex : 0);
  };

  const renderPhoto = ({
    photo,
    imageProps: { alt, style, ...restImageProps },
  }) => (
    <ButtonBase
      onClick={() => onPhotoClicked(photo)}
      style={{ width: style.width }}
    >
      <BazarImage
        alt={alt}
        style={{ ...style, width: "100%", padding: 0 }}
        {...restImageProps}
      />
    </ButtonBase>
  );

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  return (
    <Card className={classes.root} elevation={3}>
      <div className={classes.header}>
        <Typography color={colors.white} fontSize={16} fontWeight={700}>
          BUILD GALLERY
        </Typography>
      </div>
      <CardContent className={classes.cardContentBody}>
        <PhotoAlbum
          spacing={1}
          columns={(containerWidth) => {
            if (containerWidth < 600) {
              return 1;
            }
            return 2;
          }}
          layout="columns"
          photos={loadedPhotos}
          renderPhoto={renderPhoto}
        />
      </CardContent>
      <ImagesGallery
        images={photos}
        isOpen={isGalleryOpen}
        activeIndex={activePhotoIndex}
        onClose={() => setIsGalleryOpen(false)}
      />
    </Card>
  );
};

export const BuildsGalleryPhotos = React.memo(component);
